import { useState, useCallback } from 'react';
import { fetchWithToken } from 'api/data';
import { AxiosRequestConfig } from 'axios';
import { useTranslate } from 'react-admin';
import routes from 'api/routes';

export interface AuditLog {
  id: string;
  table_name: string;
  record_id: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  user_id: string;
  user_name?: string;
  user_email?: string;
  created_at: string;
  changes?: Record<string, any>;
  old_data?: Record<string, any>;
  new_data?: Record<string, any>;
  related_data?: Record<string, any>;
}

export interface AuditStats {
  total_records: number;
  actions: Record<string, number>;
  top_users: Array<{
    user_id: string;
    user_name?: string;
    count: number;
  }>;
  period_activity: Array<{
    date: string;
    count: number;
  }>;
}

export const useAudit = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const translate = useTranslate();

  const fetchHistory = useCallback((
    tableName: string, 
    recordId: string,
    fromDate?: string,
    toDate?: string
  ): Promise<AuditLog[]> => {
    setLoading(true);
    setError(null);
    
    const config: AxiosRequestConfig = {
      url: routes.auditHistory(tableName, recordId),
      method: 'get',
      params: {}
    };

    if (fromDate) config.params.from = fromDate;
    if (toDate) config.params.to = toDate;

    return fetchWithToken(config)
      .then(response => response.data?.data || [])
      .catch(err => {
        const errorMessage = err instanceof Error ? err.message : translate('resources.audit.loading_error');
        setError(errorMessage);
        console.error('Error fetching audit history:', err);
        return [];
      })
      .finally(() => setLoading(false));
  }, [translate]);

  const fetchFieldHistory = useCallback((
    tableName: string,
    recordId: string,
    fieldName: string
  ): Promise<any[]> => {
    setLoading(true);
    setError(null);
    
    const config: AxiosRequestConfig = {
      url: routes.auditFieldHistory(tableName, recordId, fieldName),
      method: 'get'
    };

    return fetchWithToken(config)
      .then(response => response.data?.field_history || [])
      .catch(err => {
        const errorMessage = err instanceof Error ? err.message : translate('resources.audit.field_loading_error');
        setError(errorMessage);
        console.error('Error fetching field audit history:', err);
        return [];
      })
      .finally(() => setLoading(false));
  }, [translate]);

  const fetchRecentChanges = useCallback((
    limit?: number,
    userId?: string,
    action?: string
  ): Promise<AuditLog[]> => {
    setLoading(true);
    setError(null);
    
    const config: AxiosRequestConfig = {
      url: routes.auditRecentChanges(),
      method: 'get',
      params: {}
    };

    if (limit) config.params.limit = limit.toString();
    if (userId) config.params.user_id = userId;
    if (action) config.params.action = action;

    return fetchWithToken(config)
      .then(response => response.data?.data || [])
      .catch(err => {
        const errorMessage = err instanceof Error ? err.message : translate('resources.audit.recent_changes_error');
        setError(errorMessage);
        console.error('Error fetching recent changes:', err);
        return [];
      })
      .finally(() => setLoading(false));
  }, [translate]);

  const fetchStats = useCallback((): Promise<AuditStats | null> => {
    setLoading(true);
    setError(null);
    
    const config: AxiosRequestConfig = {
      url: routes.auditStatistics(),
      method: 'get'
    };

    return fetchWithToken(config)
      .then(response => response.data?.data || null)
      .catch(err => {
        const errorMessage = err instanceof Error ? err.message : translate('resources.audit.stats_error');
        setError(errorMessage);
        console.error('Error fetching audit stats:', err);
        return null;
      })
      .finally(() => setLoading(false));
  }, [translate]);

  const restoreToDate = useCallback((
    tableName: string,
    recordId: string,
    date: string
  ): Promise<any> => {
    setLoading(true);
    setError(null);
    
    const config: AxiosRequestConfig = {
      url: routes.auditRestore(tableName, recordId),
      method: 'get',
      params: { date }
    };

    return fetchWithToken(config)
      .then(response => response.data?.data || null)
      .catch(err => {
        const errorMessage = err instanceof Error ? err.message : translate('resources.audit.restore_error');
        setError(errorMessage);
        console.error('Error restoring record:', err);
        return null;
      })
      .finally(() => setLoading(false));
  }, [translate]);

  return {
    loading,
    error,
    fetchHistory,
    fetchFieldHistory,
    fetchRecentChanges,
    fetchStats,
    restoreToDate,
  };
};
